import { executeSettleExpenseIntake } from '../../expenses/application/expenseIntake'
import {
  assertPaymentOnReceiveForSettle,
  type PurchasePaymentOnReceive,
} from './purchaseReceptionPayment'
import { voidExpense, listExpensesForBranch } from '../../expenses/infra/expensesRepo';
import { getProductById } from '../../catalog/infra-api'
import { getSupplierByIdLocal } from '../../catalog/infra-api';
import { getFiscalProfile } from '../../fiscal/infra-api';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { dueDateFromPaymentTerms } from '../domain/purchaseSupplierFicha';
import {
  type CreatePurchaseOrderInput,
  type CreatePurchaseOrderResult,
  type VoidPurchaseOrderInput,
  type ClosePurchaseOrderInput,
  type CreatePurchaseReceptionInput,
  type CreatePurchaseReceptionResult,
  type VoidPurchaseReceptionInput,
  type CreatePurchaseReturnInput,
  type CreatePurchaseReturnResult,
  type VoidPurchaseReturnInput,
} from '../infra/purchasesRepoMutations';
import { getPurchaseOrderById, getReceptionById, listReturnsForReception } from '../infra/purchasesRepoRead';
import { round2, splitPurchaseUnitCost, type PurchaseTaxRegime } from '../domain/purchaseIva';
import {
  canClosePurchaseOrder,
  formatPurchaseOrderNumber,
  validateCloseReason,
} from '../domain/purchaseValidation';
import { PurchaseNotFoundError, PurchaseValidationError } from '../domain/purchaseErrors';
import type {
  CreateStockTransferInput,
  CreateStockTransferResult,
  VoidStockTransferInput,
  CreateStockTransferItemInput,
} from '../domain/stockTransferTypes';
import { notifyStockDataChanged, transferStock, rejectTransfer } from '../../inventory/public-api';
import type { SyncEntity, SyncOperation } from '../../sync/core/syncTypes';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { getWebPurchasesSnapshotCache } from '../infra/webPurchasesSnapshotCache';
import { submitCommand } from '../../sync/core/submitCommand';
import { pullPurchasesWhenNeeded } from './pullPurchasesWhenNeeded';

function buildOperationId(entityId: string): string {
  return entityId;
}

function purchaseOpForEntity(entity: SyncEntity): string {
  if (entity === 'purchase_reception') return 'purchases.receive';
  if (entity === 'purchase_reception_with_expense') return 'purchases.receive';
  if (entity === 'purchase_reception_void') return 'purchases.receive_void';
  if (entity === 'purchase_order_void') return 'purchases.order_void';
  if (entity === 'purchase_order_close') return 'purchases.order_close';
  if (entity === 'purchase_return') return 'purchases.return';
  if (entity === 'purchase_return_void') return 'purchases.return_void';
  if (entity === 'branch_transfer') return 'purchases.transfer';
  if (entity === 'branch_transfer_void') return 'purchases.transfer_void';
  return 'purchases.order';
}

function purchaseItemIdFromPayload(payload: Record<string, unknown>): string | undefined {
  for (const key of [
    'purchaseOrderId',
    'receptionId',
    'returnId',
    'transferId',
    'id',
  ]) {
    const value = payload[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return undefined;
}

async function submitPurchaseCommand(params: {
  entity: SyncEntity;
  operation: SyncOperation;
  payload: Record<string, unknown>;
  branchId: string;
  failMessage: string;
}): Promise<void> {
  const result = await submitCommand({
    entity: params.entity,
    operation: params.operation,
    payload: params.payload,
  });
  if (!result.applied) {
    const error = new Error(result.ingestError?.trim() || params.failMessage);
    const itemId = purchaseItemIdFromPayload(params.payload);
    captureOpsFailure({
      module: 'purchases',
      op: purchaseOpForEntity(params.entity),
      error,
      ingestError: result.ingestError,
      applied: false,
      path: 'web-ram',
      itemId,
      extra: {
        entity: params.entity,
        branchId: params.branchId,
      },
    });
    throw error;
  }
  await pullPurchasesWhenNeeded(params.branchId, { force: true, requireFresh: true }).catch((err) => {
    captureOpsFailure({
      module: 'purchases',
      op: purchaseOpForEntity(params.entity),
      error: err,
      applied: true,
      path: 'web-ram',
      itemId: purchaseItemIdFromPayload(params.payload),
      extra: { phase: 'pull_post_write', branchId: params.branchId },
    });
  });
}

async function computeReceptionExpenseAmountGross(
  branchId: string,
  items: CreatePurchaseReceptionInput['items'],
): Promise<number> {
  let regime: PurchaseTaxRegime = 'monotributo';
  try {
    const profile = await getFiscalProfile(branchId);
    if (profile?.taxRegime === 'responsable_inscripto') regime = 'responsable_inscripto';
  } catch {
    // offline / sin fiscal → bruto
  }
  let total = 0;
  for (const item of items) {
    const product = item.productId ? await getProductById(item.productId) : null;
    const taxClass =
      product && 'taxClass' in product
        ? (product as { taxClass?: string | null }).taxClass
        : null;
    const split = splitPurchaseUnitCost({
      enteredUnitCost: item.unitCost ?? 0,
      taxClass,
      regime,
    });
    total = round2(total + split.unitCostGross * item.quantity);
  }
  return total;
}

function nextOrderNumberFromWebCache(): string {
  const year2 = new Date().getFullYear() % 100;
  const prefix = `OC-${String(year2).padStart(2, '0')}-`;
  const cached = getWebPurchasesSnapshotCache();
  let max = 0;
  for (const raw of cached?.purchaseOrders ?? []) {
    const num = String(raw.order_number ?? '');
    if (!num.startsWith(prefix)) continue;
    const n = Number(num.slice(prefix.length));
    if (Number.isFinite(n) && n > max) max = n;
  }
  return formatPurchaseOrderNumber(year2, max + 1);
}

// ─── Purchase Order ──────────────────────────────────────────────────────────

export async function createPurchaseOrderIntake(
  params: CreatePurchaseOrderInput,
): Promise<CreatePurchaseOrderResult> {
  const total = params.items.reduce(
          (sum, item) => round2(sum + item.quantity * (item.unitCost ?? 0)),
          0
        );
        // #744: no calcular orderNumber localmente — el backend lo asigna
        // atómicamente cuando recibe orderNumber: null. El pull post-submit
        // trae el número real al cache.
        const items = params.items.map((item) => ({
          itemId: item.itemId ?? crypto.randomUUID(),
          quantity: item.quantity,
          unitCost: item.unitCost,
        }));
        await submitPurchaseCommand({
          entity: 'purchase_order',
          operation: 'insert',
          branchId: params.branchId,
          failMessage: 'No se pudo crear la orden en el servidor. Reintentá.',
          payload: {
            purchaseOrderId: params.purchaseOrderId,
            operationId: buildOperationId(params.purchaseOrderId),
            branchId: params.branchId,
            supplierId: params.supplierId,
            supplierName: params.supplierName,
            status: params.status ?? 'draft',
            origin: params.origin ?? 'order',
            orderNumber: null,
            notes: params.notes,
            expectedDelivery: params.expectedDelivery,
            supplierInvoiceRef: params.supplierInvoiceRef,
            createdAt: params.createdAt ?? new Date().toISOString(),
            createdBy: params.createdBy,
            items: params.items.map((item, index) => ({
              itemId: items[index]?.itemId,
              productId: item.productId,
              productName: item.productName,
              productSku: item.productSku,
              quantity: item.quantity,
              unitCost: item.unitCost,
              unitOfMeasure: item.unitOfMeasure,
              targetBranchId: item.targetBranchId ?? null,
              // #694: variante de destino (null = parent)
              variantId: item.variantId ?? null,
            })),
          },
        });
        return {
          purchaseOrderId: params.purchaseOrderId,
          orderNumber: null,
          total,
          items,
        };
}

export async function sendPurchaseOrderIntake(
  purchaseOrderId: string,
  updatedBy?: string | null,
): Promise<void> {
  const full = await getPurchaseOrderById(purchaseOrderId);
  if (!full) return;

  if (full.status !== 'draft') {
          throw new PurchaseValidationError('El pedido cambió de estado en otro dispositivo. Recargá la lista.');
        }
        await submitPurchaseCommand({
          entity: 'purchase_order',
          operation: 'update',
          branchId: full.branchId,
          failMessage: 'No se pudo enviar la orden al servidor. Reintentá.',
          payload: {
            purchaseOrderId,
            operationId: `${purchaseOrderId}:send`,
            branchId: full.branchId,
            supplierId: full.supplierId,
            supplierName: full.supplierName,
            status: 'sent',
            origin: full.origin,
            orderNumber: full.orderNumber,
            notes: full.notes,
            expectedDelivery: full.expectedDelivery,
            supplierInvoiceRef: full.supplierInvoiceRef,
            updatedBy: updatedBy ?? null,
            items: full.items.map((item) => ({
              itemId: item.id,
              productId: item.productId,
              productName: item.productName,
              productSku: item.productSku,
              quantity: item.quantity,
              unitCost: item.unitCost,
              unitOfMeasure: item.unitOfMeasure,
              targetBranchId: item.targetBranchId ?? null,
              // #694: variante de destino (null = parent)
              variantId: item.variantId ?? null,
            })),
          },
        });
        return;
}

export async function voidPurchaseOrderIntake(
  params: VoidPurchaseOrderInput,
): Promise<void> {
  const full = await getPurchaseOrderById(params.purchaseOrderId);
        if (!full) throw new PurchaseNotFoundError('No encontré la orden.');
        await submitPurchaseCommand({
          entity: 'purchase_order_void',
          operation: 'insert',
          branchId: full.branchId,
          failMessage: 'No se pudo anular la orden en el servidor. Reintentá.',
          payload: {
            purchaseOrderId: params.purchaseOrderId,
            branchId: full.branchId,
            operationId: `${params.purchaseOrderId}:void`,
            voidedAt: params.voidedAt ?? new Date().toISOString(),
            voidedBy: params.voidedBy,
            voidReason: params.voidReason,
          },
        });
        return;
}

export async function closePurchaseOrderIntake(
  params: ClosePurchaseOrderInput,
): Promise<void> {
  const full = await getPurchaseOrderById(params.purchaseOrderId);
  if (!full) {
    throw new PurchaseNotFoundError('No encontré la orden.');
  }
  if (!canClosePurchaseOrder(full.status, full.items)) {
    throw new PurchaseValidationError('Este pedido no se puede cerrar.');
  }
  const reason = validateCloseReason(params.closeReason);
  if (!reason.ok) {
    throw new PurchaseValidationError(reason.message);
  }

  const closedAt = params.closedAt ?? new Date().toISOString();

  await submitPurchaseCommand({
          entity: 'purchase_order_close',
          operation: 'insert',
          branchId: full.branchId,
          failMessage: 'No se pudo cerrar la orden en el servidor. Reintentá.',
          payload: {
            purchaseOrderId: params.purchaseOrderId,
            operationId: `${params.purchaseOrderId}:close`,
            branchId: full.branchId,
            closeReason: params.closeReason.trim(),
            closedAt,
            closedBy: params.closedBy ?? null,
          },
        });
        return;
}

function buildLinkedExpenseRpcPayload(params: {
  expenseId: string;
  branchId: string;
  amount: number;
  expenseDate: string;
  dueDate: string | null;
  description: string;
  supplierId?: string | null;
  purchaseOrderId?: string | null;
  stockReceptionId: string;
  createdBy?: string | null;
  fiscal?: import('../../expenses/domain/expenseTypes').ExpenseFiscalInvoiceInput | null;
}): Record<string, unknown> {
  const createdAt = new Date().toISOString();
  return {
    expenseId: params.expenseId,
    operationId: params.expenseId,
    branchId: params.branchId,
    categorySlug: 'supplier_payment',
    amount: params.amount,
    expenseDate: params.expenseDate,
    description: params.description.trim() || null,
    receiptRef: null,
    supplierId: params.supplierId ?? null,
    paymentStatus: 'unpaid',
    dueDate: params.dueDate?.trim() || null,
    purchaseOrderId: params.purchaseOrderId ?? null,
    stockReceptionId: params.stockReceptionId,
    createdAt,
    createdBy: params.createdBy ?? null,
    invoiceDocType: params.fiscal?.docType ?? null,
    invoicePv: params.fiscal?.pointOfSale ?? null,
    invoiceNumber: params.fiscal?.invoiceNumber ?? null,
    supplierTaxId: params.fiscal?.supplierTaxId ?? null,
    supplierName: params.fiscal?.supplierName ?? null,
    invoiceIvaBreakdown: params.fiscal?.ivaBreakdown ?? null,
    invoicePerceptions: params.fiscal?.perceptions ?? null,
  };
}

// ─── Purchase Reception ──────────────────────────────────────────────────────

export async function createPurchaseReceptionIntake(
  params: CreatePurchaseReceptionInput & {
    generateExpense?: boolean;
    alreadyPaid?: boolean;
    paymentOnReceive?: PurchasePaymentOnReceive | null;
    fiscal?: import('../../expenses/domain/expenseTypes').ExpenseFiscalInvoiceInput | null;
  },
): Promise<CreatePurchaseReceptionResult> {
  const { generateExpense, alreadyPaid, paymentOnReceive, fiscal, ...receptionParams } = params;
  const mustGenerateExpense = generateExpense === true || fiscal != null;

  const receivedAt = params.receivedAt ?? new Date().toISOString();
        const mappedItems = params.items.map((item) => ({
          itemId: item.itemId ?? crypto.randomUUID(),
          purchaseOrderItemId: item.purchaseOrderItemId,
          productId: item.productId,
          productName: item.productName,
          productSku: item.productSku,
          quantity: item.quantity,
          unitCost: item.unitCost,
          unitOfMeasure: item.unitOfMeasure,
          targetBranchId: item.targetBranchId ?? null,
          // #694: stock por variante (el servidor lo aplica vía apply_stock_adjustment_event)
          variantId: item.variantId ?? null,
        }));
        const expenseAmountGross = await computeReceptionExpenseAmountGross(params.branchId, params.items);

        const receptionPayload = {
          receptionId: params.receptionId,
          branchId: params.branchId,
          purchaseOrderId: params.purchaseOrderId,
          supplierId: params.supplierId,
          supplierName: params.supplierName,
          receivedAt,
          receivedBy: params.receivedBy,
          notes: params.notes,
          supplierInvoiceRef: params.supplierInvoiceRef,
          items: mappedItems,
        };

        let linkedExpenseId: string | null = null;

        const organizationId = mustGenerateExpense ? await getSessionOrganizationId() : null;
        const useAtomicExpense =
          mustGenerateExpense && organizationId && expenseAmountGross > 0;

        if (useAtomicExpense) {
          let dueDate: string | null = null;
          if (params.supplierId) {
            try {
              const supplier = await getSupplierByIdLocal(params.supplierId);
              dueDate = dueDateFromPaymentTerms(supplier?.paymentTerms ?? null, receivedAt.slice(0, 10));
            } catch {
              dueDate = null;
            }
          }
          const expenseId = crypto.randomUUID();
          linkedExpenseId = expenseId;
          await submitPurchaseCommand({
            entity: 'purchase_reception_with_expense',
            operation: 'insert',
            branchId: params.branchId,
            failMessage: 'No se pudo registrar la recepción y el gasto en el servidor. Reintentá.',
            payload: {
              ...receptionPayload,
              operationId: `${params.receptionId}:with-expense`,
              linkedExpense: buildLinkedExpenseRpcPayload({
                expenseId,
                branchId: params.branchId,
                amount: expenseAmountGross,
                expenseDate: receivedAt.slice(0, 10),
                dueDate,
                description: `OC · ${params.supplierName ?? 'Proveedor'}`,
                supplierId: params.supplierId,
                purchaseOrderId: params.purchaseOrderId,
                stockReceptionId: params.receptionId,
                createdBy: params.receivedBy,
                fiscal: fiscal ?? null,
              }),
            },
          });

          if (alreadyPaid && linkedExpenseId) {
            assertPaymentOnReceiveForSettle(paymentOnReceive);
            const pay = paymentOnReceive;
            await executeSettleExpenseIntake({
              expenseId: linkedExpenseId,
              branchId: params.branchId,
              method: pay.method,
              amount: expenseAmountGross,
              categorySlug: 'supplier_payment',
              cashSessionId: pay.cashSessionId ?? null,
              note: pay.note ?? null,
              createdBy: params.receivedBy,
              paidAt: pay.paidAt ?? receivedAt,
            });
          }
        } else if (mustGenerateExpense && expenseAmountGross > 0) {
          throw new Error(
            'No pudimos registrar la recepción con el gasto del proveedor. Reintentá.',
          );
        } else {
          await submitPurchaseCommand({
            entity: 'purchase_reception',
            operation: 'insert',
            branchId: params.branchId,
            failMessage: 'No se pudo registrar la recepción en el servidor. Reintentá.',
            payload: {
              ...receptionPayload,
              operationId: buildOperationId(params.receptionId),
            },
          });
        }

        notifyStockDataChanged();
        return {
          receptionId: params.receptionId,
          expenseAmountGross,
          items: mappedItems.map((item) => ({ itemId: item.itemId, stockMovementId: item.itemId })),
          expenseId: linkedExpenseId,
        };
  // F2: opcionalmente generar gasto de proveedor al recibir
}

export async function voidPurchaseReceptionIntake(
  params: VoidPurchaseReceptionInput,
): Promise<void> {
  const activeReturns = await listReturnsForReception(params.receptionId);
        if (activeReturns.length > 0) {
          throw new PurchaseValidationError('No se puede anular una recepción con devoluciones activas. Anulá las devoluciones primero.');
        }

        const reception = await getReceptionById(params.receptionId);
        if (!reception) throw new PurchaseNotFoundError('No encontré la recepción.');

        await submitPurchaseCommand({
          entity: 'purchase_reception_void',
          operation: 'insert',
          branchId: reception.branchId,
          failMessage: 'No se pudo anular la recepción en el servidor. Reintentá.',
          payload: {
            receptionId: params.receptionId,
            operationId: `${params.receptionId}:void`,
            branchId: reception.branchId,
            voidedAt: params.voidedAt ?? new Date().toISOString(),
            voidedBy: params.voidedBy,
            voidReason: params.voidReason,
            fiscalVoidAck: params.fiscalVoidAck === true,
          },
        });

        try {
          const linkedExpenses = await listExpensesForBranch({
            branchId: reception.branchId,
            supplierId: reception.supplierId ?? undefined,
          });
          const expenseToVoid = linkedExpenses.find(
            (e) => e.status === 'active' && e.stockReceptionId === params.receptionId,
          );
          if (expenseToVoid) {
            await voidExpense({
              expenseId: expenseToVoid.id,
              branchId: reception.branchId,
              voidedBy: params.voidedBy ?? undefined,
              voidReason: `Recepción anulada: ${params.voidReason ?? 'sin motivo'}`,
            });
          }
        } catch (err) {
          captureOpsFailure({
            module: 'purchases',
            op: 'purchases.receive_void',
            error: err,
            applied: true,
            path: 'web-ram',
            itemId: params.receptionId,
            extra: { phase: 'void_linked_expense', branchId: reception.branchId },
          });
        }

        notifyStockDataChanged();
        return;
  // BUG-19: Anular el gasto ligado a esta recepción (si existe)
}

// ─── Purchase Return ─────────────────────────────────────────────────────────

export async function createPurchaseReturnIntake(
  params: CreatePurchaseReturnInput,
): Promise<CreatePurchaseReturnResult> {
  const returnedAt = params.returnedAt ?? new Date().toISOString();
        const mappedItems = params.items.map((item) => ({
          itemId: item.itemId ?? crypto.randomUUID(),
          receptionItemId: item.receptionItemId,
          productId: item.productId,
          productName: item.productName,
          productSku: item.productSku,
          quantity: item.quantity,
          unitCost: item.unitCost,
          unitOfMeasure: item.unitOfMeasure,
        }));

        await submitPurchaseCommand({
          entity: 'purchase_return',
          operation: 'insert',
          branchId: params.branchId,
          failMessage: 'No se pudo registrar la devolución en el servidor. Reintentá.',
          payload: {
            returnId: params.returnId,
            operationId: buildOperationId(params.returnId),
            branchId: params.branchId,
            receptionId: params.receptionId,
            supplierId: params.supplierId,
            supplierName: params.supplierName,
            returnedAt,
            returnedBy: params.returnedBy,
            reason: params.reason,
            notes: params.notes,
            items: mappedItems,
          },
        });

        notifyStockDataChanged();
        return {
          returnId: params.returnId,
          items: mappedItems.map((item) => ({ itemId: item.itemId, stockMovementId: item.itemId })),
        };
}

export async function voidPurchaseReturnIntake(
  params: VoidPurchaseReturnInput,
): Promise<void> {
  const cached = getWebPurchasesSnapshotCache();
        const raw = cached?.returns.find((r) => String(r.id) === params.returnId);
        const branchId = raw?.branch_id != null ? String(raw.branch_id) : '';
        if (!branchId) throw new PurchaseNotFoundError('No encontré la devolución.');

        await submitPurchaseCommand({
          entity: 'purchase_return_void',
          operation: 'insert',
          branchId,
          failMessage: 'No se pudo anular la devolución en el servidor. Reintentá.',
          payload: {
            returnId: params.returnId,
            operationId: `${params.returnId}:void`,
            voidedAt: params.voidedAt ?? new Date().toISOString(),
            voidedBy: params.voidedBy,
            voidReason: params.voidReason,
          },
        });

        notifyStockDataChanged();
        return;
}

// ─── Stock transfers entre sucursales (F5) ──────────────────────────────────
// Owner: Inventory (`sync_apply_stock_transfer`). El path legacy `branch_transfer`
// → `pos.apply_stock_transfer_command` fue droppeado (20261730130000). Compras
// delega al public-api de Inventory (ADR-036).
export async function createStockTransferIntake(
  params: CreateStockTransferInput,
): Promise<CreateStockTransferResult> {
  const mappedItems = params.items.map((item: CreateStockTransferItemInput) => ({
    itemId: item.itemId ?? crypto.randomUUID(),
    productId: item.productId,
    productName: item.productName,
    productSku: item.productSku,
    quantity: item.quantity,
    unitOfMeasure: item.unitOfMeasure,
  }));

  for (const item of mappedItems) {
    try {
      await transferStock({
        productId: item.productId,
        fromBranchId: params.fromBranchId,
        toBranchId: params.toBranchId,
        quantity: item.quantity,
        performedByUserId: params.transferredBy ?? null,
      });
    } catch (err) {
      captureOpsFailure({
        module: 'purchases',
        op: 'purchases.transfer',
        error: err,
        applied: false,
        path: 'web-ram',
        itemId: params.transferId,
        extra: {
          productId: item.productId,
          fromBranchId: params.fromBranchId,
          toBranchId: params.toBranchId,
        },
      });
      throw err instanceof Error
        ? err
        : new Error('No se pudo registrar la transferencia en el servidor. Reintentá.');
    }
  }

  notifyStockDataChanged();
  return {
    transferId: params.transferId,
    items: mappedItems.map((item) => ({ itemId: item.itemId })),
  };
}

export async function voidStockTransferIntake(
  params: VoidStockTransferInput,
): Promise<void> {
  // Solo transferencias pendientes (`sent`) se pueden rechazar vía Inventory.
  // Transferencias `completed` no tienen void en el motor canónico.
  try {
    await rejectTransfer(params.transferId, {
      performedByUserId: params.voidedBy ?? null,
    });
  } catch (err) {
    captureOpsFailure({
      module: 'purchases',
      op: 'purchases.transfer_void',
      error: err,
      applied: false,
      path: 'web-ram',
      itemId: params.transferId,
      extra: { transferId: params.transferId },
    });
    throw err instanceof Error
      ? err
      : new Error('No se pudo anular la transferencia. Reintentá desde Stock.');
  }

        notifyStockDataChanged();
        return;
}
